/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect} from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import {useLocation, useParams} from 'react-router-dom'
import {parse} from 'qs'

// Components
import {Box, Button, Stack, Link, Container, Alert, AlertTitle, AlertDescription, SimpleGrid} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import Hero from '@salesforce/retail-react-app/app/components/hero'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import Section from '@salesforce/retail-react-app/app/components/section'
import ProductScroller from '@salesforce/retail-react-app/app/components/product-scroller'
import PreviewCard from '../../components/preview-card';
import VideoPlayer from '../../components/video-player';

// Others
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

//Hooks
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'

// Constants
import {
    MAX_CACHE_AGE,
    HOME_SHOP_PRODUCTS_CATEGORY_ID,
    HOME_SHOP_PRODUCTS_LIMIT,
    CUSTOM_HOME_TITLE
} from '../../constants'

import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useProductSearch} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

// Conscia
import {getCustomerSegment, getCustomerId, getTemplate} from '../../conscia/conscia-service';
import BillboardBanner from '../../components/billboard-banner';
import AnnouncementBanner from '../../components/announcement-banner';

/**
 * This is the home page for Retail React App.
 * The page is created for demonstration purposes.
 * The page renders SEO metadata and a few promotion
 * categories and products, data is from local file.
 */
const Home = () => {
    const {
        data: {email}
    } = useCurrentCustomer()

    const intl = useIntl();
    const einstein = useEinstein();
    const {pathname} = useLocation();
    const customerSegment = getCustomerSegment();
    const externalId = getCustomerId();

    const [consciaBanner, setConsciaBanner] = React.useState('');
    const [consciaBannerLayout, setConsciaBannerLayout] = React.useState('');
    const [consciaAnnouncement, setConsciaAnnouncement] = React.useState('');
    const [consciaVideo, setConsciaVideo] = React.useState('');
    const [consciaProducts, setConsciaProducts] = React.useState('');
    const [consciaBlogs, setConsciaBlogs] = React.useState('');
    const [isConsciaLoading, setIsConsciaLoading] = React.useState(true);
    const [consciaProductMeta, setConsciaProductMeta] = React.useState({})
    const location = useLocation()
    const urlParams = new URLSearchParams(location.search);
    const queryParams = parse(urlParams.toString())
    // useServerContext is a special hook introduced in v3 PWA Kit SDK.
    // It replaces the legacy `getProps` and provide a react hook interface for SSR.
    // it returns the request and response objects on the server side,
    // and these objects are undefined on the client side.
    const {res} = useServerContext();
    if (res) {
        res.set('Cache-Control', `max-age=${MAX_CACHE_AGE}`)
    }

    const {data: productSearchResult, isLoading: isSalesforceLoading } = useProductSearch({
        parameters: {
            refine: [`cgid=${HOME_SHOP_PRODUCTS_CATEGORY_ID}`, 'htype=master'],
            limit: HOME_SHOP_PRODUCTS_LIMIT
        }
    });


    useEffect(() => {
        const context = {
            externalId: `email:${email}`,
            customerSegment,
            ...queryParams
        };
        getTemplate('home-page-model', context).then((data) => {
            console.log(data);
            setIsConsciaLoading(false);
            setConsciaProducts(data.components['model-featured-products'].response.map((e) => e.legacy));
            setConsciaProductMeta(data.components['model-featured-products']['@extras'].rule.attributes)
            setConsciaBanner(data.components['model-hero-banner'].response);
            setConsciaBannerLayout(data.components['page-layout']['@extras'].rule.attributes['hero-banner-layout'])
            setConsciaVideo(data.components["model-featured-video"].response);
            setConsciaAnnouncement(data.components["model-announcement"].response);
            setConsciaBlogs(data.components["model-articles"].response);
        });
    }, []);
    console.log( "conscia Video",consciaVideo)
    return (
        <Box>
            {consciaAnnouncement && (<AnnouncementBanner
                title={consciaAnnouncement.title}
                message={consciaAnnouncement.message}
            />)}

            {consciaBanner && (<div className='conscia-banner' >
                <BillboardBanner 
                title={consciaBanner.title}
                subtitle={consciaBanner.subtitle}
                img={{
                    src: consciaBanner.image,
                    alt: consciaBanner.title,
                }}
                layout={consciaBannerLayout}
            /></div>)}

            <Box data-testid="home-page" layerStyle="page">
                <Seo
                    title="Home Page"
                    description="Commerce Cloud Retail React App"
                    keywords="Commerce Cloud, Retail React App, React Storefront"
                />

                {consciaVideo && (<VideoPlayer 
                    videoUrl={consciaVideo.videoUrl}
                    alt={consciaVideo.alt}
                    title={consciaVideo.title}
                    height='300px'
                />)}
                {consciaProducts && (
                    <Section
                        padding={4}
                        paddingTop={16}
                        title={consciaProductMeta?.title}
                        subtitle={consciaProductMeta?.subtitle}
                    >
                        <Stack pt={8} spacing={16}>
                            <ProductScroller
                                products={consciaProducts}
                                isLoading={isConsciaLoading}
                            />
                        </Stack>
                    </Section>
                )}

                {consciaBlogs && (
                    <Section
                        padding={4}
                        paddingTop={16}
                        title={consciaBlogs["@extras"]?.rule.attributes.title || 'Featured Articles'}
                        subtitle={consciaBlogs["@extras"]?.rule.attributes.subtitle}
                    >
                        <Container maxW={'6xl'} marginTop={10}>
                            <SimpleGrid columns={{base: 1, md: 3}} spacing={10}>
                                {consciaBlogs.map((blog) => {
                                    return (<PreviewCard
                                        img={{
                                            src: blog.img,
                                            alt: blog.title
                                        }}
                                        title={blog.title}
                                        href={blog.url}
                                        description={blog.description}
                                        metadata={[blog.author, new Date(blog.publishedDate).toLocaleDateString()]}
                                    />)
                                })}
                            </SimpleGrid>
                        </Container>
                    </Section>
                )}
            </Box>
        </Box>
    )
}

Home.getTemplateName = () => 'home';

export default Home;
